import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import type { Exercise, ExerciseCategory, ExerciseSummary, Plan, Session } from "../types";
import ExerciseRunner from "../components/exercises/ExerciseRunner";
import GenerationLoader from "../components/GenerationLoader";
import Dropdown from "../components/Dropdown";

const CATEGORIES: ExerciseCategory[] = ["grammar", "vocabulary", "pronunciation", "syntax", "reading", "writing"];

type Tab = "all" | ExerciseCategory;

export default function ExercisesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionParam = searchParams.get("session");
  const sessionId = sessionParam ? Number(sessionParam) : undefined;

  const [tab, setTab] = useState<Tab>("all");
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [summary, setSummary] = useState<ExerciseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);

  const canPronounce = plan === "maestro";
  const hasSessions = sessions.some((s) => s.ended_at);

  // Per-user session number (oldest = 1), matching the dashboard SessionsTable.
  // Display uses this ordinal; navigation/URL params keep the real DB id.
  const ordinalById = useMemo(() => {
    const map = new Map<number, number>();
    [...sessions]
      .sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime())
      .forEach((s, i) => map.set(s.id, i + 1));
    return map;
  }, [sessions]);

  const autoGenerateRef = useRef(searchParams.get("generate") === "true");

  const load = useCallback(async () => {
    const [list, sum] = await Promise.all([
      api.getExercises({
        sessionId,
        category: tab === "all" ? undefined : tab,
        status: "pending",
      }),
      api.getExerciseSummary(sessionId),
    ]);
    setExercises(list);
    setSummary(sum);
  }, [tab, sessionId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  useEffect(() => {
    api.getUsage().then((u) => setPlan(u.plan)).catch(() => { /* assume non-maestro */ });
  }, []);

  useEffect(() => {
    api.getSessions(50).then(setSessions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!autoGenerateRef.current) return;
    const next = new URLSearchParams(searchParams);
    next.delete("generate");
    setSearchParams(next, { replace: true });
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      // The server builds the full per-session set (error-derived exercises plus
      // one reading and one writing) in a background task — the POST returns
      // immediately and a repeat call joins the running generation, so neither
      // proxy timeouts nor double-clicks can duplicate exercises. Poll until done.
      await api.generateExercises(sessionId);
      let status = await api.getGenerateStatus();
      while (status.running) {
        await new Promise((r) => setTimeout(r, 2000));
        status = await api.getGenerateStatus();
      }
      await load();
    } catch (err) {
      console.error("Generation failed:", err);
    } finally {
      setGenerating(false);
    }
  };

  const practice = async (kind: "reading" | "pronunciation" | "writing") => {
    setGenerating(true);
    try {
      await (kind === "reading"
        ? api.generateReading(sessionId)
        : kind === "pronunciation"
          ? api.generatePronunciation(sessionId)
          : api.generateWriting(sessionId));
      setTab(kind);
      await load();
    } catch (err) {
      console.error("Practice generation failed:", err);
    } finally {
      setGenerating(false);
    }
  };

  const clearSessionFilter = () => {
    searchParams.delete("session");
    setSearchParams(searchParams, { replace: true });
  };

  // When a category's set is fully cleared, jump to the next category that still
  // has pending exercises (respecting the active session filter).
  const advanceToNextCategory = useCallback(async () => {
    if (tab === "all") return;
    try {
      const remaining = await api.getExercises({ sessionId, status: "pending" });
      const left = new Set(remaining.map((e) => e.category));
      const start = CATEGORIES.indexOf(tab as ExerciseCategory);
      for (let i = start + 1; i < CATEGORIES.length; i++) {
        if (left.has(CATEGORIES[i])) {
          setTab(CATEGORIES[i]);
          return;
        }
      }
    } catch (err) {
      console.error("Auto-advance failed:", err);
    }
  }, [tab, sessionId]);

  const totalPending = summary?.total_pending ?? 0;
  const countForTab = (t: Tab): number => {
    if (!summary) return 0;
    if (t === "all") return summary.total_pending;
    return summary[t]?.pending ?? 0;
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto w-full px-5 sm:px-8 py-10">
        {sessionId != null && (
          <button
            onClick={() => navigate(`/dashboard?session=${sessionId}`)}
            className="btn-quiet text-xs px-4 py-2 mb-6"
          >
            ← Back to session {ordinalById.get(sessionId) ?? sessionId}
          </button>
        )}

        <header className="mb-7 animate-rise flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 relative z-10">
          <div className="min-w-0">
            <h1>Exercises</h1>
            <p className="text-text-tertiary text-[0.9375rem] mt-1">
              Targeted practice built from the mistakes you've made.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Dropdown
              label={<span className="truncate w-28">
                {sessionId != null
                  ? `#${ordinalById.get(sessionId) ?? sessionId} · ${sessions.find((s) => s.id === sessionId)?.ended_at ? new Date(sessions.find((s) => s.id === sessionId)!.ended_at!).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}`
                  : "All sessions"}
              </span>}
              buttonClassName="w-36"
            >
              <button
                onClick={clearSessionFilter}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${sessionId == null ? "text-ink bg-surface2" : "text-text-secondary hover:bg-surface2 hover:text-ink"}`}
              >
                All sessions
              </button>
              {sessions.filter((s) => s.ended_at).map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set("session", String(s.id));
                    setSearchParams(next, { replace: true });
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${sessionId === s.id ? "text-ink bg-surface2" : "text-text-secondary hover:bg-surface2 hover:text-ink"}`}
                >
                  #{ordinalById.get(s.id) ?? s.id} · {new Date(s.ended_at!).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </button>
              ))}
            </Dropdown>
            <Dropdown
              label="Generate More"
              disabled={generating || !hasSessions}
              title={!hasSessions ? "Complete a conversation session first" : undefined}
            >
              <button
                onClick={() => void practice("reading")}
                className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-surface2 hover:text-ink transition-colors"
              >
                Reading
              </button>
              <button
                onClick={() => void practice("writing")}
                className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-surface2 hover:text-ink transition-colors"
              >
                Writing
              </button>
              {canPronounce ? (
                <button
                  onClick={() => void practice("pronunciation")}
                  className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-surface2 hover:text-ink transition-colors"
                >
                  Pronunciation
                </button>
              ) : (
                <button
                  onClick={() => navigate("/settings")}
                  className="w-full text-left px-4 py-2.5 text-sm text-text-tertiary hover:bg-surface2 transition-colors flex items-center justify-between gap-2"
                  title="Pronunciation exercises are available on the Maestro plan"
                >
                  <span>Pronunciation</span>
                  <span className="text-[0.6875rem] font-semibold text-accent uppercase tracking-wide">Maestro</span>
                </button>
              )}
              <div className="border-t border-border-soft" />
              <button
                onClick={() => void generate()}
                className="w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-surface2 hover:text-ink transition-colors"
              >
                Grammar & vocabulary
              </button>
            </Dropdown>
          </div>
        </header>

        {/* Category tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-7">
          {(["all", ...CATEGORIES] as Tab[]).map((t) => {
            const n = countForTab(t);
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3.5 py-1.5 rounded-full text-[0.8125rem] font-medium whitespace-nowrap transition-colors capitalize ${
                  tab === t ? "bg-ink text-cream" : "text-text-tertiary hover:text-ink hover:bg-surface2"
                }`}
              >
                {t === "all" ? "All" : t}
                {n > 0 && (
                  <span className={`ml-1.5 text-[0.7rem] ${tab === t ? "text-cream/70" : "text-text-tertiary/70"}`}>
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <p className="text-text-tertiary italic text-sm">Loading…</p>
        ) : generating ? (
          <div className="card px-6 py-10 text-center">
            <GenerationLoader />
          </div>
        ) : exercises.length === 0 ? (
          <div className="card px-6 py-10 text-center">
            <p className="text-text-tertiary text-sm leading-relaxed max-w-sm mx-auto mb-5">
              {totalPending === 0
                ? "No exercises yet. Generate a set from your past mistakes, or choose a type from the Generate More menu above."
                : "Nothing pending in this category. Try another tab."}
            </p>
            {totalPending === 0 && (
              <>
                <button onClick={generate} disabled={!hasSessions} className="btn-primary text-sm">
                  Generate exercises
                </button>
                {!hasSessions && (
                  <p className="text-text-tertiary text-xs mt-3">Complete a conversation session first to generate exercises from your mistakes.</p>
                )}
              </>
            )}
          </div>
        ) : (
          <ExerciseRunner
            exercises={exercises}
            onItemDone={() => { api.getExerciseSummary(sessionId).then(setSummary).catch(() => {}); }}
            onSetComplete={advanceToNextCategory}
          />
        )}
      </div>
    </div>
  );
}
